import React, { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  StyledCitationNumber,
  StyledCitationNumberWrapper,
  StyledCitationValueViewer,
  StyledCitationValueViewerWrapper,
} from './CitationValueViewer.styles';
import CitationPopover from '../../../../../components/Popovers/citation-popover/CitationPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';
import { useCitationContext } from '../captable-table/CitationContext';

interface CitationValueViewerProps {
  value: string | number;
  citationReferences?: CitationReferenceData[];
  citationNumber?: number;
  citation?: CitationReferenceData;
  originalValueViewer?: React.ComponentType<any>;
  cell: CapTableCellWithReferences;
  [key: string]: any; // gross
}

const CitationValueViewer = ({
  value,
  citationReferences,
  citationNumber,
  originalValueViewer: OriginalValueViewer,
  citation,
  ...restProps
}: CitationValueViewerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredCitation, setHoveredCitation] = useState<CitationReferenceData | undefined>(undefined);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setActiveCitation, activeCitation } = useCitationContext();
  const { getSourceNumber } = useDocumentFootnotes();

  const citationSourceNumbers = useMemo(() => {
    if (!citationReferences || citationReferences.length === 0) {
      return [];
    }

    const sourceNumbers: number[] = [];
    citationReferences.forEach(citation => {
      const sourceNumber = getSourceNumber(citation.document_reference);
      if (sourceNumber !== undefined && !sourceNumbers.includes(sourceNumber)) {
        sourceNumbers.push(sourceNumber);
      }
    });

    return sourceNumbers.sort((a, b) => a - b);
  }, [citationReferences, getSourceNumber]);

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => {
      setHoveredCitation(undefined);
      setAnchorEl(null);
    }, 120);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const titleCell = useMemo(() => {
    const columnId = restProps.cell.columnLegend;
    const titleCellForColumn = restProps.cell.sheet?.cells?.[`${columnId}1`];
    return titleCellForColumn?.value ? (titleCellForColumn.value as string) : 'Unknown...';
  }, [restProps.cell]);

  const handleBadgeClick = (e: React.MouseEvent<HTMLElement>, citation: CitationReferenceData) => {
    e.stopPropagation();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setHoveredCitation(citation);
  };

  const handleCitationDelete = (citationId: number) => {
    const newCitationReferences = restProps.cell.citationReferences?.filter(citation => citation.id !== citationId);

    // eslint-disable-next-line no-param-reassign
    restProps.cell.citationReferences = newCitationReferences;

    // eslint-disable-next-line no-param-reassign
    restProps.cell.valueViewer = null;

    // eslint-disable-next-line no-param-reassign
    restProps.cell.dispatchEvent();
  };

  const citationContainsActive = useMemo(() => {
    const refForActive = citationReferences?.find(cit => cit.id === activeCitation?.id);

    return Boolean(refForActive);
  }, [citationReferences, activeCitation]);

  if (!citationReferences || citationReferences.length === 0) {
    // If no citations, render original viewer or plain text
    if (OriginalValueViewer) {
      return <OriginalValueViewer value={value} {...restProps} />;
    }
    return <FastValueViewer value={value} {...restProps} />;
  }

  return (
    <StyledCitationValueViewerWrapper
      onMouseEnter={() => {
        cancelClose();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        scheduleClose();
        setIsHovered(false);
      }}>
      {(isHovered || Boolean(hoveredCitation) || citationContainsActive) && (
        <StyledCitationNumberWrapper>
          {citationReferences.map(cit => {
            const isActiveOrHighlighted = hoveredCitation?.id === cit.id || activeCitation?.id === cit.id;

            return (
              <StyledCitationNumber
                key={cit.id}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => handleBadgeClick(e, cit)}
                sx={{
                  maxWidth: citationSourceNumbers.length > 1 ? '40px' : '24px',
                  backgroundColor: isActiveOrHighlighted ? 'primary.main' : 'primary.light',
                  color: isActiveOrHighlighted ? 'primary.contrastText' : 'primary.dark',
                  cursor: 'pointer',
                }}>
                {getSourceNumber(cit.document_reference)}
              </StyledCitationNumber>
            );
          })}
        </StyledCitationNumberWrapper>
      )}

      <StyledCitationValueViewer>
        {OriginalValueViewer ? (
          <OriginalValueViewer value={value} {...restProps} />
        ) : (
          <FastValueViewer className="text-cell" value={value} {...restProps} />
        )}
      </StyledCitationValueViewer>

      {hoveredCitation && (
        <CitationPopover
          onView={citation => setActiveCitation(citation)}
          open={Boolean(hoveredCitation)}
          anchorEl={anchorEl}
          onClose={scheduleClose}
          citation={hoveredCitation}
          columnarIdentifier={titleCell}
          onDelete={handleCitationDelete}
          slotProps={{
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
            },
          }}
        />
      )}
    </StyledCitationValueViewerWrapper>
  );
};

export default CitationValueViewer;
